import {
  addMonths,
  addWeeks,
  addYears,
  endOfISOWeek,
  endOfMonth,
  endOfYear,
  format,
  parseISO,
  startOfISOWeek,
  startOfMonth,
  startOfYear,
} from "date-fns";
import { fr } from "date-fns/locale";
import type { PeriodStats, StatsDateRange, StatsGranularity } from "../../api/stats/Stats.typed";
import { MONTH_NAMES_FR } from "../../utils/dateTime";
import { colorTokens } from "../../styles/globalStyles";

export const STATS_FIRST_DATE = new Date(2025, 0, 1);

export const GRANULARITY_OPTIONS: { value: StatsGranularity; label: string }[] = [
  { value: "week", label: "Semaine" },
  { value: "month", label: "Mois" },
  { value: "year", label: "Année" },
];

/** Monthly rows feed the year charts. */
export const BREAKDOWN_GRANULARITY: Record<StatsGranularity, StatsGranularity | null> = {
  week: null,
  month: null,
  year: "month",
};

export type PeriodBounds = { start: Date; end: Date };

export function periodBounds(granularity: StatsGranularity, anchor: Date): PeriodBounds {
  switch (granularity) {
    case "week":
      return { start: startOfISOWeek(anchor), end: endOfISOWeek(anchor) };
    case "month":
      return { start: startOfMonth(anchor), end: endOfMonth(anchor) };
    case "year":
      return { start: startOfYear(anchor), end: endOfYear(anchor) };
  }
}

export function shiftPeriod(granularity: StatsGranularity, anchor: Date, step: number): Date {
  switch (granularity) {
    case "week":
      return addWeeks(anchor, step);
    case "month":
      return addMonths(anchor, step);
    case "year":
      return addYears(anchor, step);
  }
}

export function toDateRange(bounds: PeriodBounds): StatsDateRange {
  return { start: format(bounds.start, "yyyy-MM-dd"), end: format(bounds.end, "yyyy-MM-dd") };
}

/** API dates are midnight UTC; keep the calendar day regardless of the browser timezone. */
export function parsePeriodStart(value: string): Date {
  return parseISO(value.slice(0, 10));
}

export function formatPeriodLabel(granularity: StatsGranularity, start: Date): string {
  switch (granularity) {
    case "week":
      return `Semaine du ${format(start, "dd/MM/yyyy")}`;
    case "month":
      return `${MONTH_NAMES_FR[start.getMonth()]} ${start.getFullYear()}`;
    case "year":
      return `Année ${start.getFullYear()}`;
  }
}

const formatPct = (value: number) => `${value.toFixed(1)}%`;

export type StatsMetricTone = "plain" | "success" | "danger" | "warning" | "muted";

export type StatsMetric = {
  label: string;
  shortLabel: string;
  tone: StatsMetricTone;
  value: (p: PeriodStats) => string;
};

export type StatsMetricGroup = { id: string; title: string; metrics: StatsMetric[] };

export const STATS_METRIC_GROUPS: Record<"instructors" | "clients", StatsMetricGroup> = {
  instructors: {
    id: "instructors",
    title: "Moniteurs",
    metrics: [
      {
        label: "Moniteurs",
        shortLabel: "Moniteurs",
        tone: "plain",
        value: (p) => String(p.unique_instructors),
      },
      {
        label: "Places moniteurs",
        shortLabel: "Places",
        tone: "muted",
        value: (p) => String(p.instructor_slots),
      },
      {
        label: "Taux d'annulation",
        shortLabel: "Taux annul.",
        tone: "danger",
        value: (p) => formatPct(p.instructor_cancel_pct),
      },
      {
        label: "Taux de non listés",
        shortLabel: "Taux non listé",
        tone: "warning",
        value: (p) => formatPct(p.instructor_unlisted_pct),
      },
    ],
  },
  clients: {
    id: "clients",
    title: "Clients",
    metrics: [
      {
        label: "Clients",
        shortLabel: "Clients",
        tone: "plain",
        value: (p) => String(p.unique_clients),
      },
      {
        label: "Places clients",
        shortLabel: "Places",
        tone: "muted",
        value: (p) => String(p.client_slots),
      },
      {
        label: "Taux d'annulation",
        shortLabel: "Taux annul.",
        tone: "danger",
        value: (p) => formatPct(p.client_cancel_pct),
      },
      {
        label: "Taux de non listés",
        shortLabel: "Taux non listé",
        tone: "warning",
        value: (p) => formatPct(p.client_unlisted_pct),
      },
    ],
  },
};

export type StatsTemplateColumn = { label: string; tone: StatsMetricTone };

export type StatsTemplateRow = { key: string; label: string; values: number[] };

export type StatsTemplateTableConfig = {
  id: string;
  title: string;
  emptyLabel: string;
  columns: StatsTemplateColumn[];
  rows: (p: PeriodStats) => StatsTemplateRow[];
  totals: (p: PeriodStats) => number[];
};

const templateKey = (id: number | null) => (id == null ? "none" : String(id));

export const STATS_TEMPLATE_TABLES: Record<
  "excursions" | "groups" | "trainings",
  StatsTemplateTableConfig
> = {
  excursions: {
    id: "excursions",
    title: "Sorties",
    emptyLabel: "Aucune sortie",
    columns: [
      { label: "Validées", tone: "success" },
      { label: "Annulées", tone: "danger" },
      { label: "Groupes", tone: "plain" },
    ],
    rows: (p) =>
      p.excursions_by_template.map((r) => ({
        key: templateKey(r.excursion_template_id),
        label: r.label,
        values: [r.done_count, r.cancelled_count, r.groups_count],
      })),
    totals: (p) => [p.done_count, p.cancelled_count, p.groups_count],
  },
  groups: {
    id: "groups",
    title: "Groupes",
    emptyLabel: "Aucun groupe",
    columns: [
      { label: "Groupes", tone: "plain" },
      { label: "Places moniteurs", tone: "muted" },
      { label: "Places clients", tone: "muted" },
    ],
    rows: (p) =>
      p.groups_by_template.map((r) => ({
        key: templateKey(r.group_template_id),
        label: r.label,
        values: [r.groups_count, r.instructor_slots, r.client_slots],
      })),
    totals: (p) => [p.groups_count, p.instructor_slots, p.client_slots],
  },
  trainings: {
    id: "trainings",
    title: "Formations",
    emptyLabel: "Aucune formation",
    columns: [
      { label: "Démarrées", tone: "plain" },
      { label: "Validées", tone: "success" },
    ],
    rows: (p) =>
      p.trainings_by_template.map((r) => ({
        key: templateKey(r.training_template_id),
        label: r.label,
        values: [r.trainings_started, r.trainings_validated],
      })),
    totals: (p) => [p.trainings_started, p.trainings_validated],
  },
};

export type StatsChartSeries = { key: keyof PeriodStats; label: string; color: string };

export type StatsMonthlyChartConfig = { id: string; title: string; series: StatsChartSeries[] };

export const STATS_MONTHLY_CHARTS: Record<
  "excursions" | "clients" | "trainings",
  StatsMonthlyChartConfig
> = {
  excursions: {
    id: "excursions",
    title: "Sorties par mois",
    series: [
      { key: "done_count", label: "Validées", color: colorTokens.status.success },
      { key: "cancelled_count", label: "Annulées", color: colorTokens.status.danger },
    ],
  },
  clients: {
    id: "clients",
    title: "Clients par mois",
    series: [{ key: "unique_clients", label: "Clients", color: colorTokens.text.title }],
  },
  trainings: {
    id: "trainings",
    title: "Formations par mois",
    series: [
      { key: "trainings_started", label: "Démarrées", color: colorTokens.text.title },
      { key: "trainings_validated", label: "Validées", color: colorTokens.status.success },
    ],
  },
};

const MONTHS_PER_YEAR = 12;

export type StatsMonthRow = { month: string } & Record<string, number | string>;

/** One row per calendar month of the year, zero-filled. */
export function buildMonthRows(rows: PeriodStats[], yearStart: Date): StatsMonthRow[] {
  const byMonth = new Map(rows.map((r) => [parsePeriodStart(r.period_start).getMonth(), r]));
  return Array.from({ length: MONTHS_PER_YEAR }, (_, index) => {
    const row = byMonth.get(index);
    const out: StatsMonthRow = {
      month: format(addMonths(yearStart, index), "LLL", { locale: fr }),
    };
    for (const chart of Object.values(STATS_MONTHLY_CHARTS)) {
      for (const serie of chart.series) {
        const value = row?.[serie.key];
        out[serie.key] = typeof value === "number" ? value : 0;
      }
    }
    return out;
  });
}
